import { useState } from 'react';

/**
 * Image — performance-optimised image component.
 *
 * Performance features:
 *  - loading="lazy"      — defers off-screen images (native browser API)
 *  - decoding="async"    — non-blocking image decode
 *  - width + height      — eliminates CLS (Cumulative Layout Shift)
 *  - fetchPriority       — "high" for LCP images (logo, hero), "low" otherwise
 *  - Fade-in on load     — smooth appearance, no flash of empty space
 *  - Skeleton shimmer    — placeholder while image loads
 *
 * Usage:
 *   import AppImage from '@components/common/Image';
 *   import { logoSvg } from '@assets';
 *
 *   // Standard lazy image
 *   <AppImage src={bannerUrl} alt="Banner" width={800} height={400} />
 *
 *   // LCP / above-fold image — disable lazy, boost priority
 *   <AppImage src={logoSvg} alt="Logo" width={180} height={66} priority />
 */

interface AppImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;
    alt: string;    
    width?: number;
    height?: number;
    priority?: boolean;
    skeleton?: boolean;
    wrapperClassName?: string;
}

function AppImage({
    src,
    alt,
    width,
    height,
    priority = false,
    skeleton = false,
    wrapperClassName = '',
    className = '',
    style,
    ...rest
}: AppImageProps) {
    const [loaded, setLoaded] = useState(false);

    return (
        <div
            className={['relative inline-block', wrapperClassName].filter(Boolean).join(' ')}
            style={width && height ? { width, height } : undefined}
        >
            {skeleton && !loaded && (
                <div
                    className="absolute inset-0 rounded bg-gray-100 animate-pulse"
                    aria-hidden="true"
                />
            )}

            <img
                src={src}
                alt={alt}
                width={width}
                height={height}
                loading={priority ? 'eager' : 'lazy'}
                decoding="async"
                // @ts-expect-error — fetchpriority is valid HTML but not yet in React typedefs
                fetchpriority={priority ? 'high' : 'low'}
                onLoad={() => setLoaded(true)}
                className={[
                    'transition-opacity duration-300 h-[40px] 2xl:h-[66px]',
                    skeleton ? (loaded ? 'opacity-100' : 'opacity-0') : '',
                    className,
                ].filter(Boolean).join(' ')}
                style={style}
                {...rest}
            />
        </div>
    );
}

export default AppImage;